

import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '@/lib/api'

import { Input } from './ui/shadcn/input'
import { FiX, FiZoomIn, FiZoomOut, FiCheck } from 'react-icons/fi'
import AssetThumbnail from './AssetThumbnail'
import { useShow } from '@/contexts/ShowContext'
import Loader from './loader'
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa'
import Button from './ui/Button'
import { Checkbox } from './ui/shadcn/checkbox'

export default function AssetsBrowser({onSelect, header='Find Assets', tags = [], showUntagged = false, mediaType='', multiSelect = false}) {
    const [search, setSearch] = useState('')
    const [filterTypes, setFilterTypes] = useState({
        video: { value: true, label: 'Videos' },
        image: { value: true, label: 'Images' },
        audio: { value: true, label: 'Audio' },
    })
    const [selectedAssets, setSelectedAssets] = useState([])
    const [assets, setAssets] = useState([])
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(20)
    const [totalPages, setTotalPages] = useState(1)
    const [totalAssets, setTotalAssets] = useState(0)
    const [loading, setLoading] = useState(true)
    const [gridCols, setGridCols] = useState(2)
    const {show} = useShow()

    const fetchAssets = async(pageIndex)=>{
        try{
            const activeMediaType = mediaType || Object.entries(filterTypes).filter(([, v]) => v.value).map(([k]) => k).join(',')
            const assetsResponse = await apiFetch(`/api/shows/${show._id}/assets?page=${pageIndex || page}&search=${search}&tags=${tags.map(tag => tag).join(',')}&showUntagged=${showUntagged}&mediaType=${activeMediaType}`)
            if (!assetsResponse.ok) throw new Error('Failed to fetch assets')
            const assetsData = await assetsResponse.json()

            setPage(assetsData.page)
            setTotalPages(assetsData.totalPages)
            setTotalAssets(assetsData.totalAssets)
            setPageSize(assetsData.pageSize)
            setAssets(assetsData.assets)
        } catch (error) {
            console.error('Error fetching assets:', error)
            setLoading(false)
        }
    }

    useEffect(()=>{
        const getData = async()=>{
            setPage(1)
            setLoading(true)
            await fetchAssets(1)
            setLoading(false)
        }
        const debounced = setTimeout(() => {
            getData()
        }, 500)
        return () => clearTimeout(debounced)
    },[search, filterTypes])


    const handlePreviousPage = async() => {
        if (page > 1) {
            setPage(page - 1)
            setLoading(true)
            await fetchAssets(page-1)
            setLoading(false)
        }
    }
    
    const handleNextPage = async () => {
        if (page < totalPages) {
            setPage(page + 1)
            setLoading(true)
            await fetchAssets(page+1)
            setLoading(false)
        }
    }

    const handleSelectAsset = (asset) => {
        if (multiSelect) {
            setSelectedAssets(prev => 
                prev.some(a => a._id === asset._id) 
                    ? prev.filter(a => a._id !== asset._id) 
                    : [...prev, asset]
            )
        } else {
            if(onSelect) onSelect(asset)
        }
    }

    const handleConfirmSelection = () => {
        if(onSelect) onSelect(selectedAssets)
    }

    const handleKeyDown = useCallback((e) => {
        if(e.key === 'Enter' && !loading) {
            if(multiSelect && selectedAssets.length > 0) {
                handleConfirmSelection()
            } else if(!multiSelect && assets.length) {
                handleSelectAsset(assets[0])
            }
        }
    }, [loading, multiSelect, selectedAssets, assets])

    useEffect(() => {
        const onKeyDown = (e) => handleKeyDown(e)
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [handleKeyDown])

    return (
        <div className='w-full h-full flex flex-col gap-4 relative overflow-hidden'>
            {header && <div className='px-4 pt-4'>
                <h1 className='text-xl font-bold border-l-4 border-primary pl-4 capitalize'>{header}</h1>
            </div>}
            <div className='flex w-full relative px-4'>
                <Input type='text' placeholder='Search' className='w-full' value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
                {search && <FiX className='w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer' onClick={() => setSearch('')} />}
            </div>
            {!mediaType && <div className='flex items-center gap-3 px-4'>
                {Object.keys(filterTypes).map((key) => (
                    <div className='flex items-center gap-2' key={key}>
                        <Checkbox name={`filter-${key}`} checked={filterTypes[key]?.value} onCheckedChange={(checked) => setFilterTypes(prev => ({...prev, [key]: {...prev[key], value: checked}}))} />
                        <label className='text-[10px] text-secondary-foreground/80 hover:text-secondary-foreground capitalize select-none cursor-pointer' onClick={() => setFilterTypes(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, {...v, value: k === key}])))}>{filterTypes[key]?.label}</label>
                    </div>
                ))}
            </div>}

            <div className='flex-1 overflow-y-auto relative px-4 py-1'>
                <div className='grid gap-4' style={{gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`}}>
                    {assets.map((asset) => {
                        const isSelected = multiSelect && selectedAssets.some(a => a._id === asset._id)
                        return (
                            <div key={asset._id} onClick={() => handleSelectAsset(asset)} className='flex flex-col gap-2 cursor-pointer'>
                                <div className={`relative w-full aspect-video bg-card rounded-md overflow-hidden ${isSelected ? 'ring-2 ring-primary' : ''}`}>
                                    <AssetThumbnail asset={asset} />
                                    {isSelected && <div className='absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5'>
                                        <FiCheck className='w-3 h-3' />
                                    </div>}
                                </div>
                                <span className='text-sm text-secondary-foreground truncate'>{asset.name}</span>
                            </div>
                        )
                    })}
                </div>
                {assets.length === 0 && <div className='flex flex-1 items-center justify-center h-full'>
                    <span className='text-secondary-foreground/80 text-sm'>No assets found</span>
                </div>}
                {loading && <Loader />}
            </div>
            <div className='flex items-center gap-2 px-4 py-2'>
                <Button type='secondary' icon={<FaAngleLeft className='w-4 h-4' />} onClick={handlePreviousPage} disabled={page === 1}/>
                <span>{page}/{totalPages}</span>
                <Button type='secondary' icon={<FaAngleRight className='w-4 h-4' />} onClick={handleNextPage} disabled={page===totalPages}/>
                <span>{`${((page - 1) * pageSize)+1}-${((page - 1) * pageSize)+pageSize > totalAssets ? totalAssets : ((page - 1) * pageSize)+pageSize} of ${totalAssets}`}</span>
                <div className='flex items-center gap-1 ml-auto'>
                    <Button type='secondary' icon={<FiZoomIn className='w-4 h-4' />} onClick={() => setGridCols(prev => Math.max(1, prev - 1))} disabled={gridCols === 1}/>
                    <Button type='secondary' icon={<FiZoomOut className='w-4 h-4' />} onClick={() => setGridCols(prev => Math.min(5, prev + 1))} disabled={gridCols === 5}/>
                    {multiSelect && <Button type='primary' onClick={handleConfirmSelection} disabled={selectedAssets.length === 0}>Select ({selectedAssets.length})</Button>}
                </div>
            </div>
            
        </div>
    )
} 